import { RootState } from 'app/store';
import AcceptTermsModal from 'components/AcceptTermsModal';
import ListLayout from 'components/ListLayout';
import Loader from 'components/Loader';
import Button from 'components/ui-kits/Button';
import DisplayText from 'components/ui-kits/DisplayText';
import Table from 'components/ui-kits/Table';
import UploadMerchantDocument from 'components/UploadMerchantDocument/UploadMerchantDocument';
import { UploadMerchantDocumentWrapper } from 'components/UploadMerchantDocument/UploadMerchantDocument.style';
import {
  MERCHANT_TYPE,
  PAYMENT_TOKEN_KEY,
  SELECTED_PLAN_KEY
} from 'const/Const';
import { MERCHANT_REGISTRATION_MESSAGE } from 'const/Message';
import useGetMerchantDocumentByScreen from 'hooks/useGetMerchantDocumentByScreen';
import React, { useEffect, useMemo, useState } from 'react';
import { useIsMutating, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { submitMerchantDocument } from 'services/documentService';
import {
  registerMerchantSubscriptionPlan,
  registerSubscriptionPlan,
  updateMerchantDescription
} from 'services/merchantService';
import { DocumentId, DocumentUploadId, UploadedDocument } from 'types/Document';
import { Merchant, MerchantSubscriptionPlan } from 'types/Merchant';
import { handleError, handleSuccess } from 'utils';

type MerchantRegistrationFormProps = {
  screenName: DocumentId['screen'];
  tableRows: { id: DocumentUploadId; label: string; isRequired: boolean }[];
  additionalNotesAboveTable: React.ReactNode;
  additionalNotesBelowTable: React.ReactNode;
  icon?: string;
  doneStatusLabelButton: string;
};

const headerTable = [
  { label: 'Justificatifs', width: '50%' },
  { label: 'Parcourir', width: '50%' }
];

const MerchantRegistrationForm = ({
  screenName,
  tableRows,
  additionalNotesAboveTable,
  additionalNotesBelowTable,
  doneStatusLabelButton,
  icon
}: MerchantRegistrationFormProps) => {
  const planState = useSelector((state: RootState) => state.subscriptionPlan);
  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const history = useHistory();
  const [isChangingSite, setIsChangingSite] = useState(false);
  const [isClickNoThanks, setIsClickNoThanks] = useState(false);

  const currentMutations = useIsMutating({ mutationKey: ['uploadDocument'] });

  const dataMerchant = queryClient.getQueryData<Merchant>('getMerchant');
  const selectedPlan = useMemo(() => {
    const merchantPlan = dataMerchant?.subscription;

    if (
      merchantPlan &&
      !!Object.keys(planState).length &&
      merchantPlan.type !== planState.type
    ) {
      return planState;
    }

    return merchantPlan ?? planState;
  }, [dataMerchant?.subscription, planState]);

  const isSpecialMerchant =
    selectedPlan.type === MERCHANT_TYPE.FREE_BOULANGERIES ||
    selectedPlan.type === MERCHANT_TYPE.STANDARD_BOULANGERIES;
  const isStandardPlan = !!selectedPlan.type?.includes('Standard');
  const isFinishSetup = !!dataMerchant?.isFinishSetup;
  const onlinePaymentEnabled = !!dataMerchant?.onlinePayment;

  const { data, isLoading, isFetching } =
    useGetMerchantDocumentByScreen(screenName);

  const { mutateAsync: mutateUpdateInfo, isLoading: isLoadingUpdateInfo } =
    useMutation({
      mutationFn: (data: { isFinishSetup?: boolean }) =>
        updateMerchantDescription(dataMerchant?.slug ?? '', data)
    });

  const { mutateAsync: mutateSubmitDocument, isLoading: isSubmittingDocument } =
    useMutation(
      ['submit-document', screenName],
      () => submitMerchantDocument(screenName),
      {
        onSuccess: () => {
          handleSuccess(MERCHANT_REGISTRATION_MESSAGE.SUBMIT_DOCUMENT_SUCCESS);
          queryClient.invalidateQueries({
            queryKey: ['getMerchant']
          });
          queryClient.invalidateQueries({
            queryKey: ['merchantDocuments', screenName]
          });

          if (screenName === 'be_online') {
            history.push({
              pathname: `/merchant-registration/online-payment`,
              state: { allowGuardBypass: true }
            });
          }

          if (
            screenName === 'online_payment_and_visible_on_merchant_list' &&
            !isStandardPlan
          ) {
            history.push({
              pathname: `/merchant-registration/plans`,
              state: { allowGuardBypass: true }
            });
          }
        },
        onError: () => {
          handleError(undefined, MERCHANT_REGISTRATION_MESSAGE.GENERAL_ERROR);
        }
      }
    );

  const {
    mutateAsync: mutateMerchantRegisterSubscriptionPlan,
    isLoading: isLoadingRegisterSubscriptionPlan
  } = useMutation({
    mutationFn: registerMerchantSubscriptionPlan,
    onError: () => handleError(),
    onSettled: () => setIsChangingSite(false)
  });
  const {
    mutateAsync: mutateRegisterSubscriptionPlan,
    isLoading: isRegisterSubscriptionPlanLoading
  } = useMutation(
    ({ slug, data }: { slug: string; data: MerchantSubscriptionPlan }) =>
      registerSubscriptionPlan(slug, data),
    {
      onSuccess() {
        queryClient.invalidateQueries({
          queryKey: ['getMerchant']
        });
      }
    }
  );

  const uploadedDocuments = useMemo(() => {
    if (!data) {
      return [];
    }

    return data['hydra:member'].map((document) => ({
      originalName: document.originalName,
      documentPath: document.documentPath,
      state: document.state,
      type: document.type,
      id: document.id
    })) as UploadedDocument[];
  }, [data]);

  const requiredFields = useMemo(
    () => tableRows.filter((row) => row.isRequired).map(({ id }) => id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const areAllFieldsFulfilled = useMemo(
    () =>
      requiredFields.every((field) =>
        uploadedDocuments.some((doc) => doc.type === field)
      ),
    [uploadedDocuments, requiredFields]
  );

  const areAllRequiredDocsUploadedAndSubmitted = uploadedDocuments.some(
    ({ state }) => state !== 'new'
  );
  const areBeOnlineDocumentsValidated =
    dataMerchant?.enabled && screenName === 'be_online';
  const areOnlinePaymentDocumentsValidated =
    dataMerchant?.onlinePayment &&
    screenName === 'online_payment_and_visible_on_merchant_list';
  const hasSubscriptionPlan = !!dataMerchant?.subscriptionPlans?.length;
  const isNewPlanSelected =
    dataMerchant?.subscription?.type === planState?.type;
  const isNoThanksDisabled =
    (isFinishSetup && isNewPlanSelected) ||
    onlinePaymentEnabled ||
    areAllRequiredDocsUploadedAndSubmitted;

  const submitButtonLabel = useMemo(() => {
    if (areBeOnlineDocumentsValidated || areOnlinePaymentDocumentsValidated) {
      return (
        <>
          {doneStatusLabelButton}
          {icon ? <span className="submit-icon">{icon}</span> : null}
        </>
      );
    }

    if (!areAllRequiredDocsUploadedAndSubmitted && screenName === 'be_online') {
      return 'Suivant';
    }

    if (
      !areAllRequiredDocsUploadedAndSubmitted &&
      screenName === 'online_payment_and_visible_on_merchant_list'
    ) {
      return 'Envoyer';
    }

    // waiting for validation
    return 'En vérification';
  }, [
    areBeOnlineDocumentsValidated,
    areOnlinePaymentDocumentsValidated,
    areAllRequiredDocsUploadedAndSubmitted,
    doneStatusLabelButton,
    screenName,
    icon
  ]);

  const handleClickSubmit = () => {
    if (screenName === 'online_payment_and_visible_on_merchant_list') {
      setIsModalOpen(true);
      return;
    }

    mutateSubmitDocument();
  };

  // handle back button bfcache that causes loader to presist the state
  // https://web.dev/articles/bfcache#spa
  useEffect(() => {
    const hideLoader = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsChangingSite(false);
      }
    };

    window.addEventListener('pageshow', hideLoader);

    return () => {
      window.removeEventListener('pageshow', hideLoader);
    };
  }, []);

  const shouldShowLoader =
    isLoading ||
    isFetching ||
    isSubmittingDocument ||
    isLoadingUpdateInfo ||
    isLoadingRegisterSubscriptionPlan ||
    isRegisterSubscriptionPlanLoading ||
    isChangingSite;

  const shouldDisableSubmitButton =
    !areAllFieldsFulfilled ||
    currentMutations > 0 ||
    areAllRequiredDocsUploadedAndSubmitted ||
    areBeOnlineDocumentsValidated ||
    areOnlinePaymentDocumentsValidated ||
    isLoadingRegisterSubscriptionPlan;

  const handleSubmitPlan = async () => {
    if (
      selectedPlan.type === MERCHANT_TYPE.STANDARD_BOULANGERIES ||
      selectedPlan.type === MERCHANT_TYPE.STANDARD
    ) {
      try {
        const data = await mutateMerchantRegisterSubscriptionPlan(
          window.origin
        );

        if (data.data) {
          localStorage.setItem(
            SELECTED_PLAN_KEY,
            JSON.stringify({ type: selectedPlan.type, id: selectedPlan.id })
          );
          localStorage.setItem(PAYMENT_TOKEN_KEY, data.data.payment_token);

          setIsChangingSite(true);
          window.location.href = data.data.redirect_url;
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      await mutateUpdateInfo(
        {
          isFinishSetup: true
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['getMerchant']
            });
            handleSuccess(
              MERCHANT_REGISTRATION_MESSAGE.SUBMIT_NO_THANKS_FREE_PLAN
            );
            history.push('/merchant-registration/plans');
          },
          onError() {
            handleError(undefined, MERCHANT_REGISTRATION_MESSAGE.GENERAL_ERROR);
          }
        }
      );
    }
  };

  const onAcceptSubmitted = async () => {
    if (selectedPlan.type?.includes('Free')) {
      await mutateRegisterSubscriptionPlan({
        slug: merchantSlug,
        data: {
          subscription: `${selectedPlan.id}`
        }
      });
    }

    if (isClickNoThanks) {
      handleSubmitPlan();
    } else {
      await mutateSubmitDocument();
      await handleSubmitPlan();
    }
  };

  return (
    <>
      {shouldShowLoader ? <Loader /> : null}
      <UploadMerchantDocumentWrapper>
        {additionalNotesAboveTable}
        <ListLayout>
          <Table header={headerTable}>
            {tableRows.map((item) => {
              const uploadedDocument = uploadedDocuments.find(
                (document) => document.type === item.id
              );
              const shouldDisableUploadDocumentButton =
                areAllRequiredDocsUploadedAndSubmitted ||
                (!!uploadedDocument && uploadedDocument.state !== 'new') ||
                areBeOnlineDocumentsValidated ||
                areOnlinePaymentDocumentsValidated ||
                false;

              return (
                <tr key={item.id}>
                  <td>
                    <DisplayText fontWeight="BOLD">{item.label}</DisplayText>
                  </td>
                  <td>
                    <div className="import-document">
                      <UploadMerchantDocument
                        uploadedDocument={uploadedDocument}
                        screenName={screenName}
                        type={item.id}
                        key={`${item.id}-${uploadedDocument?.originalName}`}
                        isDisabled={shouldDisableUploadDocumentButton}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <DisplayText margin="1.5rem 0" fontStyle="italic">
                  * Ce fichier est requis et doit obligatoirement être
                  téléchargé pour compléter le processus.
                </DisplayText>
              </td>
              <td align="right" rowSpan={1} colSpan={1}>
                <>
                  {screenName ===
                    'online_payment_and_visible_on_merchant_list' && (
                    <Button
                      variant="cancel"
                      type="button"
                      fontWeight="BOLD"
                      borderRadius="10rem"
                      style={{ marginRight: '3rem' }}
                      onClick={() => {
                        setIsModalOpen(true);
                        setIsClickNoThanks(true);
                      }}
                      isDisabled={isNoThanksDisabled}>
                      Non merci
                    </Button>
                  )}
                  <Button
                    fontWeight="BOLD"
                    isDisabled={shouldDisableSubmitButton}
                    type="submit"
                    borderRadius="10rem"
                    variant="primary"
                    onClick={() => {
                      setIsClickNoThanks(false);
                      handleClickSubmit();
                    }}>
                    {submitButtonLabel}
                  </Button>
                </>
              </td>
            </tr>
          </Table>
        </ListLayout>
        {additionalNotesBelowTable}
      </UploadMerchantDocumentWrapper>
      {screenName === 'online_payment_and_visible_on_merchant_list' &&
        isModalOpen && (
          <AcceptTermsModal
            screenName={screenName}
            merchantType={selectedPlan.type}
            isClickNoThanks={isClickNoThanks}
            isSpecialMerchant={isSpecialMerchant}
            onClose={() => setIsModalOpen(false)}
            hasSubscriptionPlan={hasSubscriptionPlan}
            onAcceptSubmitted={onAcceptSubmitted}
            onCustomAcceptNoThanks={async () => {
              await mutateSubmitDocument();
              await handleSubmitPlan();
            }}
          />
        )}
    </>
  );
};

export default MerchantRegistrationForm;
